import { useContext } from 'react';
import AppContext from '../../../AppContext';
import RollingRowData from './RollingRowData';
import styles from './RollingRow.module.css';
import { RatioFormat, DollarFormat, PointFormat, TextFormat } from '../../format';
import { DataResult } from '../../model/DataResult';
import RowHelmet from '../RowHelmet';
import LabelRow from '../LabelRow';
import BlankRow from '../BlankRow/BlankRow';
import { selectSurplusRatio } from '../surplusRatioUtils';
import ISiteLink from '../../links/ISiteLink';
import { computeQuarterYearPairs } from '../quarterDateUtils';
import { buildScoringColumnUrl } from '../linkUrlUtils';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

function rowClassSwitch(rowClass: string = '') {
  switch (rowClass) {
    case 'INDENT':
      return 'indent';
    case 'DOUBLE_INDENT':
      return 'double_indent';
    case 'TOTAL':
      return 'total';
    case 'GRAND_TOTAL':
      return 'grand_total';
    case 'BOLD':
      return 'bold';
    default:
      return '';
  }
}

function hasAnyOutsideBenchmarks(data: RollingRowData) {
  return (
    data.percentOrPointChange?.outsideBenchmark ||
    data.dollarChange?.outsideBenchmark ||
    data.percentOfPHS?.outsideBenchmark ||
    data.percentOfCAS?.outsideBenchmark ||
    data.percentOfNAA?.outsideBenchmark ||
    data.percentOfTDP?.outsideBenchmark ||
    data.currentQuarter?.outsideBenchmark ||
    data.priorQuarter?.outsideBenchmark ||
    data.priorQuarterMinusOne?.outsideBenchmark ||
    data.priorQuarterMinusTwo?.outsideBenchmark ||
    data.priorQuarterMinusThree?.outsideBenchmark
  );
}

function getDataDisplayForCurrentRepository(
  riskRepository: string | null,
  rowDisplay: RollingRowData['rowDisplay']
) {
  if (riskRepository === 'FR' || !rowDisplay || !riskRepository) {
    return '';
  }
  const displayValue = rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay];
  if (displayValue === 'GREY') return 'grey';
  if (displayValue === 'HIDE') return 'hidden';
  return '';
}

function renderQuarterValue(data: DataResult | undefined, rowType: string) {
  if (data?.showAsNA) {
    return <p className="column_align_right">NA</p>;
  }
  if (rowType === 'TEXT') {
    return <TextFormat {...data} />;
  }
  if (rowType === 'RATIO' || rowType === 'ABSOLUTE_RATIO' || rowType === 'IRIS_RATIO') {
    return <RatioFormat {...data} />;
  }
  return <DollarFormat {...data} />;
}

function renderQuarterLink(data: DataResult | undefined, url: string) {
  if (!data || (data.value == null && data.textValue == null)) {
    return <p className="column_align_right"></p>;
  }
  if (data.showAsNA) {
    return <p className="column_align_right">NA</p>;
  }
  const linkLabel = data.textValue ?? data.value;
  return (
    <p className="column_align_right">
      <ISiteLink relativeUrl={url} label={linkLabel} />
    </p>
  );
}

export default function RollingRow({
  rowKey,
  rowType,
  rowClass,
  label,
  benchmark,
  rowDisplay,
  calculateDetail,
  rowDataSource,
  rowLinkConfiguration,
  scoringStatementType,
  linkURL,
  industryMean,
  percentOrPointChange,
  dollarChange,
  percentOfPHS,
  percentOfCAS,
  percentOfNAA,
  percentOfTDP,
  percentOfCurrentYear,
  qtrPercentOfCAS,
  qtrPercentOfNAA,
  qtrPercentOfPHS,
  qtrPercentOfTDP,
  qtrPercentOfCASPlusAVR,
  qtrPercentOfPYECAS,
  qtrPercentOfPYEPHS,
  currentQuarter,
  priorQuarter,
  priorQuarterMinusOne,
  priorQuarterMinusTwo,
  priorQuarterMinusThree,
  sectionKey,
  forceRollingPrior,
  calculatePYIndustryAvg,
  quarter,
  comparableColumns,
  showChangeColumns = true,
  showPercentPointChange,
  showDollarChange
}: RollingRowData & {
  calculatePYIndustryAvg?: boolean;
  quarter?: string;
  comparableColumns?: boolean;
  showChangeColumns?: boolean;
  showPercentPointChange?: boolean;
  showDollarChange?: boolean;
}) {
  const { showBenchmark, riskRepository, cocode, statementType, year } = useContext(AppContext);

  if (rowType === 'BLANK') {
    return <BlankRow />;
  }

  if (!label || label.trim().toLowerCase() === 'missing') {
    return null;
  }

  const repoDisplay = getDataDisplayForCurrentRepository(riskRepository, rowDisplay);

  const className = cx(
    'container',
    { collapse: !showBenchmark },
    repoDisplay,
    rowClassSwitch(rowClass)
  );

  if (rowType === 'LABEL') {
    return (
      <div
        id={
          'label-' +
          label
            .replace(/\s+/g, '-')
            .replace(/[^a-zA-Z0-9-]+/g, '')
            .toLowerCase()
        }
        className={className}
        data-testid="rolling-data-row">
        <RowHelmet label={label} rowLinkConfiguration={rowLinkConfiguration} />
        <LabelRow />
      </div>
    );
  }

  const data: RollingRowData = {
    rowKey,
    rowType,
    rowClass,
    label,
    benchmark,
    rowDisplay,
    calculateDetail,
    rowDataSource,
    rowLinkConfiguration,
    scoringStatementType,
    percentOrPointChange,
    dollarChange,
    percentOfPHS,
    percentOfCAS,
    percentOfNAA,
    percentOfTDP,
    percentOfCurrentYear,
    currentQuarter,
    priorQuarter,
    priorQuarterMinusOne,
    priorQuarterMinusTwo,
    priorQuarterMinusThree
  };

  const surplusRatio = selectSurplusRatio({
    percentOfPHS: qtrPercentOfPHS || percentOfPHS,
    percentOfCAS: qtrPercentOfCAS || percentOfCAS,
    percentOfCurrentYear,
    percentOfTDP: qtrPercentOfTDP || percentOfTDP,
    percentOfPYECAS: qtrPercentOfPYECAS,
    percentOfPYEPHS: qtrPercentOfPYEPHS,
    percentOfCASPlusAVR: qtrPercentOfCASPlusAVR
  });

  const effectivePercentOfNAA = qtrPercentOfNAA || percentOfNAA;

  const isRatioType =
    rowType === 'RATIO' || rowType === 'ABSOLUTE_RATIO' || rowType === 'IRIS_RATIO';

  // For LINK rows, build a per-column iSite URL. Each quarterly column links to its own quarter's
  // report: reuse the annual link builder (appends COCODE + DATA_YEAR) and substitute REPORT=0N
  // with that column's quarter. Rolling vs comparable decides each column's (quarter, year) window.
  const columnLinkUrls =
    rowType === 'LINK'
      ? computeQuarterYearPairs(year, quarter ?? 'Q3', !!comparableColumns).map((pair) =>
          buildScoringColumnUrl(
            linkURL ?? '',
            statementType?.statementTypeCode ?? '',
            cocode,
            pair.year,
            pair.quarter
          )
        )
      : [];

  function renderChangeColumns() {
    if (!showChangeColumns) {
      return <><p></p><p></p></>;
    }
    const pctCell =
      showPercentPointChange === false ? (
        <p></p>
      ) : isRatioType ? (
        <PointFormat {...percentOrPointChange} showDefaultValue={true} />
      ) : (
        <RatioFormat {...percentOrPointChange} showDefaultValue={true} />
      );
    const dollarCell =
      showDollarChange === false || isRatioType ? (
        <p></p>
      ) : (
        <DollarFormat {...dollarChange} showDefaultValue={true} />
      );
    return <>{pctCell}{dollarCell}</>;
  }

  function getRowId() {
    if (scoringStatementType) {
      return `${sectionKey}-${rowKey}-${scoringStatementType}`;
    }
    return sectionKey ? `${sectionKey}-${rowKey}` : `rowKey-${rowKey}`;
  }

  return (
    <div id={getRowId()} className={className} data-testid="rolling-data-row">
      <RowHelmet
        label={label}
        rowKey={rowKey}
        isOutsideBenchmark={hasAnyOutsideBenchmarks(data)}
        benchmarkText={benchmark}
        calculateDetail={calculateDetail}
        rowType={rowType}
        rowDataSource={rowDataSource}
        rowLinkConfiguration={rowLinkConfiguration}
        sectionKey={sectionKey}
        forceRollingPrior={forceRollingPrior}
      />
      {/* Change columns (%/Pt Change + $ Change) render only when the subsection has a
          corresponding change flag — matching the header, which is blank otherwise. */}
      {renderChangeColumns()}
      {/* Surplus slot: PY Industry Avg (when calculatePYIndustryAvg) or the surplus ratio. */}
      {calculatePYIndustryAvg ? (
        <RatioFormat {...industryMean} showDefaultValue={true} />
      ) : surplusRatio ? (
        <RatioFormat {...surplusRatio} />
      ) : (
        <p></p>
      )}
      <RatioFormat {...effectivePercentOfNAA} />
      {rowType === 'LINK' ? (
        <>
          {renderQuarterLink(currentQuarter, columnLinkUrls[0])}
          {renderQuarterLink(priorQuarter, columnLinkUrls[1])}
          {renderQuarterLink(priorQuarterMinusOne, columnLinkUrls[2])}
          {renderQuarterLink(priorQuarterMinusTwo, columnLinkUrls[3])}
          {renderQuarterLink(priorQuarterMinusThree, columnLinkUrls[4])}
        </>
      ) : (
        <>
          {renderQuarterValue(currentQuarter, rowType)}
          {renderQuarterValue(priorQuarter, rowType)}
          {renderQuarterValue(priorQuarterMinusOne, rowType)}
          {renderQuarterValue(priorQuarterMinusTwo, rowType)}
          {renderQuarterValue(priorQuarterMinusThree, rowType)}
        </>
      )}
    </div>
  );
}
